import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CalendarCheck, Users } from 'lucide-react';
import { formatCurrency } from '../../utils/revenueFormatters';

interface RevenueBySource {
  spotts: { revenue: number; bookings: number };
  manual: { revenue: number; bookings: number };
  recurring: { revenue: number; bookings: number };
  memberships: { revenue: number; bookings: number };
}

interface RevenueCompositionChartProps {
  revenueBySource: RevenueBySource;
}

const BOOKING_COLOR = '#3b82f6'; // blue-500
const MEMBERSHIP_COLOR = '#8b5cf6'; // purple-500

export function RevenueCompositionChart({ revenueBySource }: RevenueCompositionChartProps) {
  const { bookingsRevenue, membershipRevenue, totalRevenue, bookingsCount, membershipCount, bookingsPct, membershipPct } = useMemo(() => {
    const bRev = (revenueBySource.spotts?.revenue || 0) + (revenueBySource.manual?.revenue || 0) + (revenueBySource.recurring?.revenue || 0);
    const mRev = revenueBySource.memberships?.revenue || 0;
    const total = bRev + mRev;
    const bCount = (revenueBySource.spotts?.bookings || 0) + (revenueBySource.manual?.bookings || 0) + (revenueBySource.recurring?.bookings || 0);
    const mCount = revenueBySource.memberships?.bookings || 0;

    return {
      bookingsRevenue: bRev,
      membershipRevenue: mRev,
      totalRevenue: total,
      bookingsCount: bCount,
      membershipCount: mCount,
      bookingsPct: total > 0 ? Math.round((bRev / total) * 100) : 0,
      membershipPct: total > 0 ? Math.round((mRev / total) * 100) : 0,
    };
  }, [revenueBySource]);

  // Don't render if no data
  if (totalRevenue === 0) {
    return null;
  }

  // Only one type has revenue — show simple card instead of chart
  const hasOnlyBookings = membershipRevenue === 0;
  const hasOnlyMemberships = bookingsRevenue === 0;

  const chartData = [
    { name: 'Bookings', value: bookingsRevenue, color: BOOKING_COLOR },
    { name: 'Memberships', value: membershipRevenue, color: MEMBERSHIP_COLOR },
  ].filter((d) => d.value > 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 text-sm">{data.name}</p>
          <p className="text-sm text-gray-600">{formatCurrency(data.value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Composition</h3>

      <div className="flex items-center gap-6">
        {/* Donut Chart */}
        {!hasOnlyBookings && !hasOnlyMemberships && (
          <div className="w-32 h-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Stats */}
        <div className="flex-1 space-y-3">
          {/* Bookings */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <CalendarCheck className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Bookings</p>
                <p className="text-sm font-bold text-gray-900">{formatCurrency(bookingsRevenue)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{bookingsCount} booking{bookingsCount !== 1 ? 's' : ''}</p>
                {!hasOnlyBookings && !hasOnlyMemberships && (
                  <p className="text-xs font-medium text-blue-600">{bookingsPct}%</p>
                )}
              </div>
            </div>
          </div>

          {/* Memberships */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Memberships</p>
                <p className="text-sm font-bold text-gray-900">{formatCurrency(membershipRevenue)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{membershipCount} membership{membershipCount !== 1 ? 's' : ''}</p>
                {!hasOnlyBookings && !hasOnlyMemberships && (
                  <p className="text-xs font-medium text-purple-600">{membershipPct}%</p>
                )}
              </div>
            </div>
          </div>

          {/* Progress bar showing split */}
          {!hasOnlyBookings && !hasOnlyMemberships && (
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden flex">
              <div
                className="h-full rounded-l-full transition-all"
                style={{ width: `${bookingsPct}%`, backgroundColor: BOOKING_COLOR }}
              />
              <div
                className="h-full rounded-r-full transition-all"
                style={{ width: `${membershipPct}%`, backgroundColor: MEMBERSHIP_COLOR }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
